import { fetchCustomFieldPagedData, manageCreateCustomField, manageDeleteCustomField, manageUpdateCustomField } from "@/lib/services/customField"
import { CustomField } from "@/lib/types/customField"
import { defineStore } from "pinia"

interface CustomFieldState {
    customFields: CustomField[]
    loading: boolean
}

export const useCustomFieldsStore = () => {
    const inner = defineStore('customFields', {
        state: (): CustomFieldState => ({
            customFields: [],
            loading: false
        }),
        actions: {
            async fetchPagedData(page: number = 1, pageSize: number, sortField?: string, sortOrder?: number, filters?: Record<string, any>): Promise<PagedResultMetadata | null> {
                this.loading = true

                try {
                    const params: PagedRequestParams = {
                        offset: (page - 1) * pageSize,
                        limit: pageSize,
                        sortField: sortField,
                        sortOrder: sortOrder && sortOrder === 1 ? 'ASC' : 'DESC',
                        filters: filters
                    }

                    const res = await fetchCustomFieldPagedData(params)
                    const { items, ...metadata } = res
                    this.customFields = items

                    return metadata as PagedResultMetadata
                } catch (err) {
                    console.error(err)
                } finally {
                    this.loading = false
                }

                return null
            },

            async fetchAll(): Promise<void> {
                this.loading = true

                try {
                    const res = await fetchCustomFieldPagedData()
                    const { items } = res
                    this.customFields = items
                } catch (err) {
                    console.error(err)
                } finally {
                    this.loading = false
                }
            },

            async createOne(data: CustomField): Promise<CustomField | ErrorMessage> {
                try {
                    const res = await manageCreateCustomField(data)
                    if (res && 'errorCode' in res) {
                        return res
                    }
                    this.customFields.push(res)
                    return res
                } catch (err) {
                    console.error(err)
                    throw err
                }
            },

            async updateOne(data: CustomField): Promise<CustomField | ErrorMessage> {
                try {
                    const res = await manageUpdateCustomField(data)

                    if (res && 'errorCode' in res) {
                        return res
                    }

                    const index = this.customFields.findIndex(cf => cf.id === res.id)
                    if (index !== -1) this.customFields[index] = res
                    return res
                } catch (err) {
                    console.error(err)
                    throw err
                }
            },

            async deleteOne(id: string): Promise<ErrorMessage | void> {
                try {
                    const res = await manageDeleteCustomField(id)
                    if (res) {
                        return res
                    }
                    this.customFields = this.customFields.filter(cf => cf.id !== id)
                } catch (err) {
                    console.error(err)
                    throw err
                }
            }
        }
    })

    const store = inner()
    return store
}
